import {
  DownloadOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  message,
  notification,
  Progress,
  Select,
  Space,
  Table,
} from 'antd';
import { PaginationConfig } from 'antd/lib/pagination';
import { parse } from 'json2csv';
import { get, isEmpty, isFunction, pick } from 'lodash';
import moment from 'moment';
import qs from 'qs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useAsync, useToggle } from 'react-use';
import styled from 'styled-components';

import {
  resetDownloadItems,
  updateDownloadItems,
} from '../../actions/DownloadAction';
import { useSAUsers } from '../../custom-hooks/sa-users';
import store from '../../store';
import { downloadFile, fetchAPI } from '../../utils/api';
import useWindowDimension from '../../utils/useWindowDimension';
import AdvanceSearch, { AdvanceSearchObject } from './AdvanceSearch';

type Props = {
  url: string;
  urlDownload?: string;
  perPageDownload?: number;
  method?: string;
  columns: any[];
  rowKey?: string;
  showAll?: boolean;
  showDateRange?: boolean;
  showDownload?: boolean;
  downloadFile?: {
    show: boolean;
    url: string;
    method: string;
    payloadParser: any;
    fileName: string;
  };
  showPagination?: boolean;
  showSearch?: boolean;
  overrideParams?: object;
  updateQueryParams?: boolean;
  extraPayload?: object;
  paginationPosition?: 'both' | 'bottom' | 'top' | 'topRight';
  pageSize?: number;
  extra?: React.ReactNode;
  dataSource?: any;
  tableProps?: any;
  rowSelectionProps?: {
    selectedRowKey?: string;
    onChange: (
      dataSource: any,
      selectedRowKeys: string[],
      selectedRows: any
    ) => void;
  };
  expandToggle?: boolean;
  version?: string;
  tableContentStyle?: {
    maxHeight?: string;
    overflow?: string;
  };
  responseParser?: (record: any) => any;
  directResultParser?: (response: any) => any;
  useInternalPurchase?: boolean;
  useFilterCreatedBy?: boolean;
  useSearchVCard?: boolean;
  advanceSearch?: AdvanceSearchObject[];
  extraPicks?: string[];
  is_public?: boolean;
};
type Result = {
  items: any[];
  total: number;
};
const PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = ['5', '10', '20', '50', '100'];
const Search = Input.Search;

const EditableContext = React.createContext<any>({});

export const EditableRow: React.FC<{
  index: number;
}> = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

export const EditableCell: React.FC<{
  title: React.ReactNode;
  editable: boolean;
  children: React.ReactNode;
  dataIndex: string;
  record: any;
  isRequired?: boolean;
  handleSave: (record: any) => void;
}> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  isRequired = true,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef();
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      // @ts-ignore
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async e => {
    try {
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {}
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[
          {
            required: isRequired ? true : false,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{ paddingRight: 24 }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const components = {
  body: {
    row: EditableRow,
    cell: EditableCell,
  },
};

const Downloader = ({ total }) => {
  const items = useSelector((state: AppState) => state.download.items);
  const percent = Math.round((items.length / total) * 100);

  return (
    <div>
      <Progress percent={percent} status="active" />
      {percent >= 100 && (
        <CSVLink filename={'download.csv'} data={parse(items)}>
          Click to Download
        </CSVLink>
      )}
    </div>
  );
};
const UserFilter = props => {
  const users = useSAUsers(props.user);
  return (
    <Select
      style={{ width: 120 }}
      onChange={props.onChange}
      placeholder="Created By"
      options={users.data?.list?.map(line => {
        return { value: line.id, label: line.full_name };
      })}
    />
  );
};
const useTable = (
  props: Props
): [React.ReactNode, () => void, any, any, () => any] => {
  const { isSmallScreen } = useWindowDimension();

  const searchParams =
    props.overrideParams ||
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    });

  const {
    q = '',
    page,
    per_page = PAGE_SIZE,
    from_date,
    to_date,
  } = searchParams;
  const history = useHistory();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState(searchParams.filters);
  const advanceSearchObject = (props.advanceSearch || []).reduce(
    (mergedObj, obj) => {
      const value = searchParams[obj.payload_key];

      return {
        ...mergedObj,
        [obj.payload_key]: obj.searchQueryParser
          ? obj.searchQueryParser(value)
          : value || obj.defaultValue,
      };
    },
    {}
  );
  const [advanceSearch, setAdvanceSearch] = useState<{
    [key: string]: string;
  }>(advanceSearchObject);
  const [keyword, setKeyword] = useState(q);
  const [search, setSearch] = useState(q);
  const [current, setCurrent] = useState(page ? Number(page) : 1);
  const [pageSize, setPageSize] = useState(
    props.pageSize ? props.pageSize : per_page ? Number(per_page) : PAGE_SIZE
  );

  const { updateQueryParams = true } = props;

  const [dateRange, setDateRange] = useState<any>(
    from_date && to_date ? [moment(from_date), moment(to_date)] : []
  );
  const [sorter, setSorter] = useState(
    searchParams.sorter
      ? { ...searchParams.sorter, ...searchParams['?sorter'] }
      : undefined
  );
  const [load, toggleLoad] = useToggle(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [data, setData] = useState({ items: [], total: 0, filters: {} });
  const [expandAll, setExpandAll] = useToggle(false);
  const [rowKeys, setRowKeys] = useState([]);
  const user = useSelector((state: AppState) => state.user);
  const [filterCreatedById, setFilterCreatedById] = useState(null);
  const [searchInternalPurchase, setSearchInternalPurchase] = useState(null);
  const [searchVCard, setSearchVCard] = useState();
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const buildPayload = () => {
    const _sorter = isEmpty(sorter)
      ? undefined
      : pick(sorter, ['columnKey', 'order']);

    // Sanitize: Ant Design filterDropdown clearFilters() sets values to null
    // Convert null filter values to [] so the backend receives valid arrays
    const safeFilters =
      filters && !isEmpty(filters)
        ? Object.fromEntries(
            Object.entries(filters).map(([k, v]) => [k, v == null ? [] : v])
          )
        : undefined;

    const payload = {
      filters: safeFilters,
      sorter: _sorter,
      per_page: pageSize,
      q: search,
      page: current,
      from_date: !isEmpty(dateRange)
        ? dateRange[0].format('YYYY-MM-DD') + 'T00:00:00+00:00'
        : undefined,
      to_date: !isEmpty(dateRange)
        ? dateRange[1].format('YYYY-MM-DD') + 'T00:00:00+00:00'
        : undefined,
      is_internal_purchase: props.useInternalPurchase
        ? searchInternalPurchase
        : undefined,
      filter_created_by_id: props.useFilterCreatedBy
        ? filterCreatedById
        : undefined,
      search_vcard: props.useSearchVCard ? searchVCard : undefined,
    };

    if (!!props.advanceSearch?.length) {
      return props.advanceSearch.reduce((payloadObj, obj) => {
        const value = advanceSearch[obj.payload_key] || obj.defaultValue;
        if (obj.payloadFormatter) {
          return obj.payloadFormatter(payloadObj, value);
        }
        return {
          ...payloadObj,
          [obj.payload_key]: value,
        };
      }, payload);
    }

    return payload;
  };
  const fetchState = useAsync<Result>(async () => {
    let payload = buildPayload();
    if (props.extraPayload) {
      if (isFunction(props.extraPayload)) {
        payload = props.extraPayload(payload);
      } else {
        payload = {
          ...payload,
          ...props.extraPayload,
        };
      }
    }

    const result = await fetchAPI(props.url, {
      method: props.method ? props.method : 'POST',
      version: props.version,
      payload,
      is_public: props.is_public,
    });
    let items = result?.items || [];
    const total = result?.total || result?.pagination?.total;
    if (props.directResultParser) {
      items = props.directResultParser(result);
    }

    setData({
      items: props.responseParser ? items.map(props.responseParser) : items,
      total,
      filters: result.filters,
    });
    if (updateQueryParams) {
      history.push({
        search: qs.stringify(payload),
      });
    }
    return result;
  }, [
    current,
    search,
    dateRange,
    pageSize,
    sorter,
    load,
    searchInternalPurchase,
    filterCreatedById,
    searchVCard,
    advanceSearch,
  ]);
  const downloadData = async total => {
    const perPage = props.perPageDownload || 200;
    const payload = buildPayload();
    const steps = total / perPage;
    notification.open({
      message: 'Download Progress',
      description: (
        <Provider store={store.store}>
          <Downloader total={total} />
        </Provider>
      ),
      duration: 0,
      onClose: () => dispatch(resetDownloadItems()),
    });
    for (let index = 0; index < steps; index++) {
      const page = index + 1;
      let _picks = [
        'id',
        ...props.columns.map(col => col.key || col.dataIndex).filter(Boolean),
      ];
      if (props.extraPicks) {
        _picks = [..._picks, ...props.extraPicks];
      }
      const data = await fetchAPI(props.urlDownload || props.url, {
        method: 'POST',
        version: props.version,
        payload: {
          ...payload,
          page,
          per_page: perPage,
          _picks,
        },
      });

      dispatch(updateDownloadItems(data.items));
    }
  };

  const triggerExpandAll = (toExpand: boolean) => {
    if (toExpand) {
      const allRows = data?.items?.map(row => String(row?.id));
      setRowKeys(allRows);
      return;
    }
    setRowKeys([]);
  };

  const handleChange = (
    pagination: PaginationConfig,
    filters: any,
    sorter: any
  ) => {
    setFilters(filters);
    setSorter(sorter);
    setCurrent(pagination.current ? pagination.current : 1);
    setPageSize(ps => pagination.pageSize || ps);
    setExpandAll(false);
  };

  const total: number = get(data, 'total', 0);
  const {
    extra,
    showAll = false,
    showDateRange = true,
    showDownload = true,
    showPagination = true,
    showSearch = true,
    paginationPosition = 'both',
    tableProps = {},
    rowSelectionProps,
  } = props;
  const { scroll: scrollProp, expandable, ...otherTableProps } = tableProps;
  const dataSource = fetchState.value
    ? fetchState.value.items
    : props.dataSource
    ? props.dataSource
    : [];

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  let rowSelection;
  if (rowSelectionProps) {
    rowSelection = {
      selectedRowKeys: !!rowSelectionProps.selectedRowKey
        ? dataSource
            .filter(item => item[rowSelectionProps.selectedRowKey])
            .map(item => String(item.id))
        : selectedRowKeys,
      onChange: (newSelectedRowKeys, newSelectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
        if (!!rowSelectionProps.onChange) {
          rowSelectionProps.onChange(
            dataSource,
            newSelectedRowKeys,
            newSelectedRows
          );
        }
      },
    };
  }

  let tableColumns = props.columns
    .filter(col => !col.hidden)
    .map(col => {
      const colKey = col.key;
      if ((!col.filters && !col.filterDropdown) || !colKey) {
        return col;
      }

      const filterValues = (searchParams?.filters || {})[colKey];
      return {
        ...col,
        defaultFilteredValue: filterValues || [],
      };
    });
  if (props?.expandToggle) {
    tableColumns = [
      {
        title: (
          <div>
            <div
              onClick={() => {
                triggerExpandAll(!expandAll);
                setExpandAll(v => !v);
              }}
            >
              <Button type="link" style={{ padding: 0 }}>
                {expandAll ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
              </Button>
            </div>
          </div>
        ),
        key: 'expand-all',
        width: 30,
        fixed: props?.tableProps?.expandable?.fixed,
      },
      ...tableColumns,
    ];
  }
  // if (data.filters) {
  //   tableColumns = tableColumns.map(col => {
  //     if (col.filterKey && get(data.filters, col.filterKey)) {
  //       return {
  //         ...col,
  //         filters: data.filters[col.filterKey],
  //       };
  //     }
  //     return col;
  //   });
  // }

  const handleChangeInternalPurchase = event => {
    setSearchInternalPurchase(event === 'internal');
  };

  const handleChangeFilterCreatedBy = event => {
    setFilterCreatedById(event);
  };

  const handleChangeSearchVcard = event => {
    setSearchVCard(event.target.checked);
  };
  const Comp = (
    <Container
      tableContentHeight={props.tableContentStyle?.maxHeight}
      tableContentOverflow={props.tableContentStyle?.overflow}
    >
      <Space
        wrap
        // split={<Divider type="vertical" />}
        style={{ marginBottom: 10 }}
      >
        {showSearch && (
          <>
            <Search
              style={{ width: 250 }}
              value={String(keyword)}
              placeholder="Search..."
              onChange={e => setKeyword(e.target.value)}
              onSearch={value => {
                setSearch(value);
                setCurrent(1);
              }}
              enterButton
              allowClear
            />
          </>
        )}
        {props.useSearchVCard && (
          <Checkbox onChange={handleChangeSearchVcard}>Search VCard</Checkbox>
        )}

        {showDateRange && (
          <React.Fragment>
            <DatePicker.RangePicker
              style={{ width: 250 }}
              onChange={(dates, dateStrings) => {
                setDateRange(dates);
                setCurrent(1);
              }}
              value={dateRange}
              ranges={{
                Today: [moment(), moment()],
                'This Month': [
                  moment().startOf('month'),
                  moment().endOf('month'),
                ],
                'This Year': [
                  moment().startOf('year'),
                  moment().endOf('month'),
                ],
              }}
            />
          </React.Fragment>
        )}

        {!!showDownload && (
          <Button
            icon={<DownloadOutlined />}
            onClick={async () => {
              dispatch(resetDownloadItems());
              notification.destroy();
              await downloadData(total);
            }}
          >
            Download data
          </Button>
        )}
        {!!props.downloadFile?.show && (
          <Button
            icon={<DownloadOutlined />}
            loading={isDownloadingFile}
            onClick={async () => {
              try {
                setIsDownloadingFile(true);
                const resp = await fetchAPI(props.downloadFile.url, {
                  method: props.downloadFile.method,
                  payload: props.downloadFile.payloadParser(buildPayload()),
                });

                downloadFile(resp, props.downloadFile.fileName);
              } catch (error) {
                message.error('Failed download file');
              } finally {
                setIsDownloadingFile(false);
              }
            }}
          >
            Download data
          </Button>
        )}
        {props.useInternalPurchase && (
          <Select
            style={{ width: 120 }}
            onChange={handleChangeInternalPurchase}
            placeholder="Type"
            options={[
              { value: 'internal', label: 'Internal' },
              { value: 'external', label: 'External' },
            ]}
          />
        )}
        {props.useFilterCreatedBy && (
          <UserFilter user={user} onChange={handleChangeFilterCreatedBy} />
        )}
        {extra}
      </Space>
      {!!props.advanceSearch?.length && (
        <AdvanceSearch
          advanceSearches={props.advanceSearch || []}
          advanceSearchState={advanceSearch}
          setAdvanceSearch={payload => setAdvanceSearch(payload)}
        />
      )}
      <Table
        bordered
        components={components}
        rowClassName={() => 'editable-row'}
        columns={tableColumns}
        dataSource={get(data, 'items')}
        loading={fetchState.loading || tableLoading}
        pagination={
          showPagination
            ? {
                current,
                pageSize,
                total,
                position: paginationPosition,
                pageSizeOptions: !!showAll
                  ? [...PAGE_SIZE_OPTIONS, String(total)]
                  : PAGE_SIZE_OPTIONS,
                showSizeChanger: true,
                onShowSizeChange: (current, size) => setPageSize(size),
                showTotal: (totalItems, range) =>
                  `${range[0]}-${range[1]} of ${totalItems} items`,
              }
            : false
        }
        onChange={handleChange}
        rowKey={(item: any) =>
          props.rowKey ? String(get(item, props.rowKey)) : String(item.id)
        }
        size="middle"
        rowSelection={rowSelection}
        {...otherTableProps}
        scroll={isSmallScreen ? {} : { ...scrollProp } || {}}
        expandable={isSmallScreen ? {} : expandable || {}}
        expandedRowKeys={rowKeys}
        onExpandedRowsChange={rows => {
          setRowKeys(rows);
        }}
      />
    </Container>
  );
  return [Comp, toggleLoad, fetchState.value, setTableLoading, buildPayload];
};

const Container = styled.div.attrs((props: any) => ({
  tableContentHeight: props.tableContentHeight || 'unset',
  tableContentOverflow: props.tableContentOverflow || 'unset',
}))`
  .editable-cell {
    position: relative;
  }

  .editable-cell-value-wrap {
    padding: 5px 12px;
    cursor: pointer;
  }

  .editable-row:hover .editable-cell-value-wrap {
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 4px 11px;
  }

  .ant-table {
    .ant-table-content {
      max-height: ${props => props.tableContentHeight};
      overflow: ${props => props.tableContentOverflow};
    }

    tr:nth-child(odd) td {
      background-color: #dfe7ff;
    }
  }

  .width-144 {
    width: 144px;
    * {
      width: 144px;
    }
  }

  .m-label {
    display: none;
    font-size: 12px;
    font-weight: bold;
    font-style: italic;
  }

  @media (max-width: 767px) {
    table {
      width: 100%;

      .m-label {
        display: block;
      }
    }
    thead {
      display: none;
    }
    tbody {
      width: 100%;
      display: table-cell;
    }
    tr {
      display: flex;
      width: 100%;
      flex-direction: column;
    }
    td {
      padding: 5px 15px !important;
      font-size: 85%;
    }
  }
`;

export default useTable;
